import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import {
  Users, CalendarDays, Wallet, AlertCircle, Cake, BellRing,
  CheckCircle2, Target, Repeat, Share2, TrendingUp
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, CartesianGrid, Legend, Area, AreaChart } from "recharts";
import { useAuth } from "@/context/AuthContext";
import PanchangCard from "@/components/PanchangCard";

const COLORS = ["#D4AF37", "#1E3A8A", "#3B82F6", "#8B5CF6", "#14B8A6", "#EF4444", "#F59E0B", "#10B981"];

const KpiCard = ({ icon: Icon, label, value, suffix = "", accent, testid, delay = 0 }) => (
  <motion.div initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay }} data-testid={testid}>
    <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 card-lift">
      <div className="flex items-start justify-between">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent}`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
      </div>
      <div className="mt-5">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">{label}</div>
        <div className="mt-1.5 font-display text-3xl font-light tracking-tight text-slate-900 dark:text-white">
          {value}<span className="text-lg text-slate-500 ml-0.5">{suffix}</span>
        </div>
      </div>
    </Card>
  </motion.div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([api.get("/dashboard/stats"), api.get("/dashboard/charts")])
      .then(([s, c]) => { if (alive) { setStats(s.data); setCharts(c.data); } })
      .catch(err => console.error("dashboard load failed:", err?.message));
    return () => { alive = false; };
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Command Center</div>
          <h1 className="mt-1 font-display text-4xl md:text-5xl font-light tracking-tight text-slate-900 dark:text-white">
            Namaste, <span className="text-amber-600 dark:text-amber-400">{user?.name?.split(" ")[0]}</span>
          </h1>
          <p className="mt-2 text-slate-500 dark:text-slate-400 text-sm">Here is a celestial view of today's consultancy.</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Today</div>
          <div className="font-mono text-slate-700 dark:text-slate-200">{new Date().toDateString()}</div>
        </div>
      </div>

      {/* KPIs */}
      <PanchangCard />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
        <KpiCard testid="kpi-total-clients" icon={Users} label="Total Clients" value={stats?.total_clients ?? "—"} accent="bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400" delay={0.02} />
        <KpiCard testid="kpi-today-appointments" icon={CalendarDays} label="Today's Appointments" value={stats?.today_appointments ?? "—"} accent="bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400" delay={0.04} />
        <KpiCard testid="kpi-monthly-revenue" icon={Wallet} label="Monthly Revenue" value={`₹${(stats?.monthly_revenue ?? 0).toLocaleString()}`} accent="bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" delay={0.06} />
        <KpiCard testid="kpi-pending-payments" icon={AlertCircle} label="Pending Payments" value={`₹${(stats?.pending_payments ?? 0).toLocaleString()}`} accent="bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400" delay={0.08} />
        <KpiCard testid="kpi-today-birthdays" icon={Cake} label="Today's Birthdays" value={stats?.today_birthdays ?? "—"} accent="bg-pink-50 dark:bg-pink-500/10 text-pink-700 dark:text-pink-400" delay={0.10} />
        <KpiCard testid="kpi-today-followups" icon={BellRing} label="Today's Follow-ups" value={stats?.today_followups ?? "—"} accent="bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400" delay={0.12} />
        <KpiCard testid="kpi-consultations-completed" icon={CheckCircle2} label="Consultations Done" value={stats?.consultations_completed ?? "—"} accent="bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-400" delay={0.14} />
        <KpiCard testid="kpi-new-leads" icon={Target} label="New Leads" value={stats?.new_leads ?? "—"} accent="bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-400" delay={0.16} />
        <KpiCard testid="kpi-repeat-clients" icon={Repeat} label="Repeat Clients" value={stats?.repeat_clients ?? "—"} accent="bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400" delay={0.18} />
        <KpiCard testid="kpi-referral-clients" icon={Share2} label="Referral Clients" value={stats?.referral_clients ?? "—"} accent="bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400" delay={0.20} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue by month */}
        <Card className="lg:col-span-2 p-6 rounded-2xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60" data-testid="chart-revenue">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Revenue by Month</div>
              <h3 className="font-display text-xl font-medium text-slate-900 dark:text-white mt-0.5">Cash flow trajectory</h3>
            </div>
            <TrendingUp className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={charts?.revenue_by_month || []}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#D4AF37" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
              <XAxis dataKey="month" stroke="#94A3B8" tick={{ fontSize: 11 }} />
              <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "rgba(15,23,42,0.95)", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12, color: "#fff" }} />
              <Area type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        {/* Consultation types */}
        <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60" data-testid="chart-consultation-types">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Consultation Types</div>
          <h3 className="font-display text-xl font-medium text-slate-900 dark:text-white mt-0.5 mb-3">Service mix</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={charts?.consultation_types || []} dataKey="count" nameKey="type" innerRadius={45} outerRadius={80} paddingAngle={2}>
                {(charts?.consultation_types || []).map((c, i) => (<Cell key={c.type || `ct-${i}`} fill={COLORS[i % COLORS.length]} />))}
              </Pie>
              <Tooltip contentStyle={{ background: "rgba(15,23,42,0.95)", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12, color: "#fff" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-1.5 mt-2">
            {(charts?.consultation_types || []).slice(0, 6).map((c, i) => (
              <div key={c.type} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="truncate">{c.type}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Lead sources */}
        <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60" data-testid="chart-lead-sources">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Lead Sources</div>
          <h3 className="font-display text-xl font-medium text-slate-900 dark:text-white mt-0.5 mb-3">Where clients discover us</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={charts?.lead_sources || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
              <XAxis dataKey="source" stroke="#94A3B8" tick={{ fontSize: 10 }} />
              <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "rgba(15,23,42,0.95)", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12, color: "#fff" }} />
              <Bar dataKey="count" fill="#1E3A8A" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Client growth */}
        <Card className="lg:col-span-2 p-6 rounded-2xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60" data-testid="chart-client-growth">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Client Growth</div>
          <h3 className="font-display text-xl font-medium text-slate-900 dark:text-white mt-0.5 mb-3">Devotees added over time</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={charts?.client_growth || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
              <XAxis dataKey="month" stroke="#94A3B8" tick={{ fontSize: 11 }} />
              <YAxis stroke="#94A3B8" tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "rgba(15,23,42,0.95)", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 12, color: "#fff" }} />
              <Line type="monotone" dataKey="clients" stroke="#3B82F6" strokeWidth={2.5} dot={{ fill: "#D4AF37", r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Birthdays panel */}
      {stats?.birthdays_list?.length > 0 && (
        <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800 bg-gradient-to-br from-pink-50/50 to-transparent dark:from-pink-500/5">
          <h3 className="font-display text-xl mb-3 flex items-center gap-2"><Cake className="w-5 h-5 text-pink-500" /> Today's Birthdays</h3>
          <div className="flex flex-wrap gap-2">
            {stats.birthdays_list.map((b) => (
              <a key={b.id} href={`/clients/${b.id}`} className="px-3 py-1.5 rounded-full border border-pink-200 dark:border-pink-500/30 bg-white dark:bg-slate-900 text-sm hover:bg-pink-50">
                {b.full_name}
              </a>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
